// src/services/opexMetrics.js
// COST-TAB-BUILD: Pure derived-metrics service for the Cost tab.
//
// Accepts opex rows + context and returns formatted cost-efficiency metrics.
// No network calls, no side effects. Memoized per unique input signature.
//
// Departments that are NOT IT contribute to the tracked total.
// IT is always excluded from cost-efficiency calculations (centrally managed).

/**
 * Format a number as a compact currency string: "18,933 USD" (no prefix symbol).
 * Returns "—" for null/undefined/NaN/0.
 *
 * @param {number|null|undefined} val
 * @param {string} [currency="USD"]  ISO 4217 code — appended as trailing suffix.
 * @returns {string}
 */
export function fmtDollars(val, currency = "USD") {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n)) + " " + currency;
}

/**
 * Format a number as a currency string with commas — full precision.
 * E.g. 7648508 with GBP → "7,648,508 GBP". Returns "—" for nullish/NaN.
 *
 * @param {number|null|undefined} val
 * @param {string} [currency="USD"]  ISO 4217 code — appended as trailing suffix.
 * @returns {string}
 */
export function fmtDollarsFull(val, currency = "USD") {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n)) + " " + currency;
}

/**
 * Format a number as a full-precision currency string with a symbol prefix and
 * no trailing currency code — for display in panels where the currency is already
 * established by context (e.g. toggle, header).
 *   9,349,224 USD → "$9,349,224"
 *   850 GBP       → "£850"
 *   1,350 EUR     → "€1,350"
 * Returns "—" for null/undefined/NaN.
 *
 * @param {number|null|undefined} val
 * @param {string} [currency="USD"]
 * @returns {string}
 */
export function fmtDollarsFullSym(val, currency = "USD") {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  const sym = CCY_SYM[String(currency).toUpperCase()] ?? (String(currency) + "\u202f");
  return sym + new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

/**
 * Format a number as a compact abbreviated currency string for tight spaces.
 *   1,200,000  → "1.2M USD"
 *   123,000,000 → "123M USD"
 *   1,500,000,000 → "1.5B USD"
 *   85,000     → "85K USD"
 *   9,500      → "9.5K USD"
 *   950        → "950 USD"
 * Returns "—" for null/undefined/NaN.
 *
 * @param {number|null|undefined} val
 * @param {string} [currency="USD"]
 * @returns {string}
 */
export function fmtDollarsAbbrev(val, currency = "USD") {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const fmt1 = (v) => {
    const s = v.toFixed(1);
    return s.endsWith(".0") ? s.slice(0, -2) : s;
  };
  if (abs >= 1_000_000_000) {
    const v = abs / 1_000_000_000;
    return sign + (v < 10 ? fmt1(v) : Math.round(v)) + "B " + currency;
  }
  if (abs >= 1_000_000) {
    const v = abs / 1_000_000;
    return sign + (v < 100 ? fmt1(v) : Math.round(v)) + "M " + currency;
  }
  if (abs >= 1_000) {
    const v = abs / 1_000;
    return sign + (v < 10 ? fmt1(v) : Math.round(v)) + "K " + currency;
  }
  return sign + Math.round(abs).toString() + " " + currency;
}

/**
 * Format a number as a compact abbreviated currency string for headline KPI
 * tiles. Uses a currency-symbol prefix ($, £, €, etc.) with no trailing code:
 *   16,400,000 USD → "$16.4M"
 *   93,492,240 GBP → "£93.5M"
 *   42,069     USD → "$42.1K"
 *   137        USD → "$137"
 * Returns "—" for null/undefined/NaN/0.
 *
 * @param {number|null|undefined} val
 * @param {string} [currency="USD"]  ISO 4217 code, looked up for symbol prefix.
 * @returns {string}
 */
const CCY_SYM = {
  USD: "$", EUR: "€", GBP: "£", JPY: "¥", CAD: "C$", AUD: "A$",
  CHF: "CHF\u202f", CNY: "¥", SGD: "S$", HKD: "HK$", MXN: "MX$",
  BRL: "R$", INR: "₹", KRW: "₩", SEK: "kr\u202f", NOK: "kr\u202f",
  DKK: "kr\u202f", NZD: "NZ$", ZAR: "R\u202f",
};
export function fmtDollarsHero(val, currency = "USD") {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return "—";
  const sym  = CCY_SYM[String(currency).toUpperCase()] ?? (String(currency) + "\u202f");
  const abs  = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  // 2 decimal places, strip trailing zeros: 9.35 → "9.35", 9.30 → "9.3", 9.00 → "9"
  const fmt2 = (v) => { const s = v.toFixed(2); return s.endsWith(".00") ? s.slice(0, -3) : s.endsWith("0") ? s.slice(0, -1) : s; };
  if (abs >= 1_000_000_000) return sign + sym + fmt2(abs / 1_000_000_000) + "B";
  if (abs >= 1_000_000)     return sign + sym + fmt2(abs / 1_000_000) + "M";
  if (abs >= 1_000)         return sign + sym + fmt2(abs / 1_000) + "K";
  return sign + sym + Math.round(abs).toLocaleString("en-US");
}

/**
 * Compute derived cost-efficiency metrics from opex rows and context.
 * Extended to support the Idea B ledger format (per-category amounts,
 * proportional bars, per-sqft efficiency, fully-loaded total).
 *
 * @param {object} params
 * @param {Array<{department:string, amountUsd:number}>} params.departments
 *   OpEx rows from the API. IT rows are present but excluded from totals.
 * @param {number|null} params.seatCount    Installed capacity.
 * @param {number|null} params.avgDaily     Avg daily attendees (most-recent month).
 * @param {string|null} params.attendanceMonth  Label like "May".
 * @param {number|null} params.leaseAnnual  Active lease annual cost.
 * @param {number|null} params.sqft         Location square footage.
 *
 * @returns {{
 *   opexTotal:                number,
 *   leaseAmt:                 number|null,
 *   wpAmt:                    number,
 *   secAmt:                   number,
 *   totalFullyLoaded:         number|null,
 *   leasePct:                 number|null,
 *   wpPct:                    number|null,
 *   secPct:                   number|null,
 *   opexPerSeat:              number|null,
 *   opexPerAttendee:          number|null,
 *   fullyLoadedTotal:         number|null,
 *   fullyLoadedPerSeat:       number|null,
 *   fullyLoadedPerAttendee:   number|null,
 *   fullyLoadedPerSqft:       number|null,
 *   basePerSeat:              number|null,
 *   basePerAttendee:          number|null,
 *   basePerSqft:              number|null,
 *   hasLeaseData:             boolean,
 *   hasAttendanceData:        boolean,
 * }}
 */
export function computeOpexMetrics({ departments, seatCount, avgDaily, leaseAnnual, sqft }) {
  const tracked = departments.filter((d) => d.department !== "it");
  const opexTotal = tracked.reduce((sum, d) => sum + parseFloat(d.amountUsd ?? 0), 0);

  // Per-category amounts
  const wpRow  = departments.find((d) => d.department === "workplace");
  const secRow = departments.find((d) => d.department === "security");
  const wpAmt  = parseFloat(wpRow?.amountUsd  ?? 0);
  const secAmt = parseFloat(secRow?.amountUsd ?? 0);

  const seats = seatCount != null && !isNaN(parseFloat(seatCount)) && parseFloat(seatCount) > 0
    ? parseFloat(seatCount)
    : null;
  const daily = avgDaily != null && !isNaN(parseFloat(avgDaily)) && parseFloat(avgDaily) > 0
    ? parseFloat(avgDaily)
    : null;
  const lease = leaseAnnual != null && !isNaN(parseFloat(leaseAnnual)) && parseFloat(leaseAnnual) > 0
    ? parseFloat(leaseAnnual)
    : null;
  const sqftN = sqft != null && !isNaN(parseFloat(sqft)) && parseFloat(sqft) > 0
    ? parseFloat(sqft)
    : null;

  // Fully-loaded total: lease + opex (wp + sec)
  const fullyLoadedTotal = lease != null ? lease + opexTotal : null;

  // Per-category proportions of the fully-loaded total
  const flTotal = fullyLoadedTotal;
  const leasePct = (flTotal != null && flTotal > 0 && lease != null) ? lease / flTotal : null;
  const wpPct    = (flTotal != null && flTotal > 0) ? wpAmt  / flTotal : null;
  const secPct   = (flTotal != null && flTotal > 0) ? secAmt / flTotal : null;

  // Legacy opex-only metrics (backward compat)
  const opexPerSeat     = seats && opexTotal > 0 ? opexTotal / seats : null;
  const opexPerAttendee = daily && opexTotal > 0 ? opexTotal / daily : null;

  // Fully-loaded efficiency
  const fullyLoadedPerSeat      = fullyLoadedTotal != null && seats ? fullyLoadedTotal / seats : null;
  const fullyLoadedPerAttendee  = fullyLoadedTotal != null && daily ? fullyLoadedTotal / daily : null;
  const fullyLoadedPerSqft      = fullyLoadedTotal != null && sqftN ? fullyLoadedTotal / sqftN : null;

  // Base efficiency (lease only)
  const basePerSeat     = lease != null && seats ? lease / seats : null;
  const basePerAttendee = lease != null && daily ? lease / daily : null;
  const basePerSqft     = lease != null && sqftN ? lease / sqftN : null;

  return {
    opexTotal,
    leaseAmt:   lease,
    wpAmt,
    secAmt,
    totalFullyLoaded: fullyLoadedTotal,
    leasePct,
    wpPct,
    secPct,
    opexPerSeat,
    opexPerAttendee,
    fullyLoadedTotal,
    fullyLoadedPerSeat,
    fullyLoadedPerAttendee,
    fullyLoadedPerSqft,
    basePerSeat,
    basePerAttendee,
    basePerSqft,
    hasLeaseData:      lease != null,
    hasAttendanceData: daily != null,
  };
}
